import React, { useState, useEffect, useRef, useMemo } from 'react';
import autoPartsService from '../services/autoPartsService';
import { dvlaLookup, motWebsiteLookup } from '../services/firebaseFunctions';
import { formatVehicleDisplayName } from '../utils/helpers';
import { useApp } from '../context/AppContext';
import { isVehicleWorkTypeVisible } from '../utils/enabledStages';
import {
  getPartCategory,
  getSupplierLinks,
  groupPartsByCategory,
} from '../services/partsSupplierLinks';

const formatVehicleOption = (v) => {
  if (!v) return '';
  const reg = v.reg || '—';
  const name = v.vehicle || 'Unknown';
  const vinLabel = v.vin ? '(VIN ✓)' : '(no VIN)';
  return `${reg} — ${name} ${vinLabel}`;
};

const supplierLinkButtonStyle = (variant) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '4px',
  minHeight: '36px',
  padding: '6px 10px',
  borderRadius: '6px',
  border: '1px solid var(--border)',
  background: variant === 'gsf' ? 'rgba(0, 102, 51, 0.08)' : 'rgba(0, 128, 0, 0.08)',
  color: variant === 'gsf' ? '#006633' : '#0a7a0a',
  fontSize: '12px',
  fontWeight: 600,
  textDecoration: 'none',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
});

const PartSupplierLinks = ({ part, vehicle, compact = false }) => {
  const links = part?.supplierLinks || getSupplierLinks(part, vehicle);
  const hasReg = Boolean(links?.reg);
  const gsfUrl = links?.gsf?.url;
  const ecpUrl = links?.euro?.url;

  if (!hasReg) {
    return (
      <div
        style={{
          marginTop: compact ? '8px' : '12px',
          fontSize: '12px',
          color: 'var(--text-muted)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        Select a vehicle with a registration to check on GSF / Euro Car Parts
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px',
        marginTop: compact ? '8px' : '12px',
        alignItems: 'center',
      }}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      {!compact && (
        <span style={{ fontSize: '12px', color: 'var(--text-muted)', marginRight: '4px' }}>
          Open with reg {links.reg}:
        </span>
      )}
      <a
        href={gsfUrl}
        target="_blank"
        rel="noopener noreferrer"
        title={`Open GSF Car Parts for ${links.reg}`}
        style={supplierLinkButtonStyle('gsf')}
      >
        {compact ? `GSF ${links.reg}` : `GSF Car Parts (${links.reg})`} ↗
      </a>
      <a
        href={ecpUrl}
        target="_blank"
        rel="noopener noreferrer"
        title={`Open Euro Car Parts for ${links.reg}`}
        style={supplierLinkButtonStyle('euro')}
      >
        {compact ? `Euro ${links.reg}` : `Euro Car Parts (${links.reg})`} ↗
      </a>
    </div>
  );
};

/**
 * Parts Lookup Component
 *
 * Uses the shared car catalog on Plutoo Hub (auto-parts-db / https://github.com/lifeofcapo/car-api).
 * Description search and load-by-vehicle use the local brands/models/parts database.
 * Requires vehicle context: either pass vehicle prop (from vehicle detail) or select from garage when standalone.
 *
 * @param {boolean} embedded - When true, render as an inline panel (page/tab) instead of a modal overlay.
 */
const PartsLookup = ({ vehicle, vehicles: vehiclesProp, onPartSelected, onClose, embedded = false }) => {
  const { vehicles: vehiclesFromContext, garageInfo } = useApp();
  const vehicles = useMemo(
    () => vehiclesProp ?? vehiclesFromContext ?? [],
    [vehiclesProp, vehiclesFromContext]
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPart, setSelectedPart] = useState(null);
  const [partDetails, setPartDetails] = useState(null);
  const [compatibleVehicles, setCompatibleVehicles] = useState([]);
  const [showingCompatibility, setShowingCompatibility] = useState(false);
  const [inlineArticleNo, setInlineArticleNo] = useState('');
  const [inlineOemNo, setInlineOemNo] = useState('');
  const [vehicleId, setVehicleId] = useState(null);
  const [vehicleIdLoading, setVehicleIdLoading] = useState(false);
  // Standalone mode: user selects a vehicle from garage; when vehicle prop is passed, that takes precedence
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  // Ad-hoc: enter reg for a car not in the garage (DVLA lookup)
  const [adHocReg, setAdHocReg] = useState('');
  const [adHocVehicle, setAdHocVehicle] = useState(null);
  const [adHocLoading, setAdHocLoading] = useState(false);
  const [adHocError, setAdHocError] = useState(null);
  const [vehiclePickerOpen, setVehiclePickerOpen] = useState(false);
  const [vehiclePickerQuery, setVehiclePickerQuery] = useState('');
  const [vehiclePickerHighlight, setVehiclePickerHighlight] = useState(0);
  const [resultsCategoryFilter, setResultsCategoryFilter] = useState('All');
  const vehiclePickerRef = useRef(null);
  const vehicleInputRef = useRef(null);

  const isStandalone = vehicle == null;
  const contextVehicle = vehicle ?? adHocVehicle ?? (vehicles.find((v) => v.id === selectedVehicleId) || null);

  const garageVehicles = useMemo(
    () => (vehicles || []).filter((v) => !v.deletedAt && isVehicleWorkTypeVisible(garageInfo, v)),
    [vehicles, garageInfo]
  );

  const selectedGarageVehicle = adHocVehicle
    ? null
    : garageVehicles.find((v) => v.id === selectedVehicleId) || null;

  const vehiclePickerDisplay = adHocVehicle
    ? `${adHocVehicle.reg} — ${adHocVehicle.vehicle}`
    : selectedGarageVehicle
      ? formatVehicleOption(selectedGarageVehicle)
      : '';

  const filteredPickerVehicles = useMemo(() => {
    const q = vehiclePickerQuery.trim().toLowerCase().replace(/\s+/g, '');
    if (!q) return garageVehicles;
    return garageVehicles.filter((v) => {
      const hay = [
        v.reg,
        v.vehicle,
        v.make,
        v.model,
        v.customerName,
        v.vin,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .replace(/\s+/g, '');
      return hay.includes(q) || (v.reg || '').toLowerCase().includes(vehiclePickerQuery.trim().toLowerCase());
    });
  }, [garageVehicles, vehiclePickerQuery]);

  const resultsWithCategories = useMemo(
    () =>
      (results || []).map((part) => ({
        ...part,
        category: part.category || getPartCategory(part),
        // Always rebuild with current vehicle — supplier sites need the reg
        supplierLinks: getSupplierLinks(part, contextVehicle),
      })),
    [results, contextVehicle]
  );

  const categoryCounts = useMemo(() => {
    const counts = { All: resultsWithCategories.length };
    for (const part of resultsWithCategories) {
      const cat = part.category || 'Other';
      counts[cat] = (counts[cat] || 0) + 1;
    }
    return counts;
  }, [resultsWithCategories]);

  const filteredResults = useMemo(() => {
    if (resultsCategoryFilter === 'All') return resultsWithCategories;
    return resultsWithCategories.filter(
      (p) => (p.category || 'Other') === resultsCategoryFilter
    );
  }, [resultsWithCategories, resultsCategoryFilter]);

  const groupedResults = useMemo(
    () => groupPartsByCategory(filteredResults),
    [filteredResults]
  );

  useEffect(() => {
    setResultsCategoryFilter('All');
  }, [results]);

  const clearVehicleSelection = () => {
    setAdHocVehicle(null);
    setAdHocReg('');
    setAdHocError(null);
    setSelectedVehicleId(null);
    setResults([]);
    setSelectedPart(null);
  };

  const selectGarageVehicle = (id) => {
    setAdHocVehicle(null);
    setAdHocReg('');
    setAdHocError(null);
    setSelectedVehicleId(id || null);
    setResults([]);
    setSelectedPart(null);
    setVehiclePickerOpen(false);
    setVehiclePickerQuery('');
    setVehiclePickerHighlight(0);
  };

  useEffect(() => {
    if (!vehiclePickerOpen) return undefined;
    const onPointerDown = (e) => {
      if (vehiclePickerRef.current && !vehiclePickerRef.current.contains(e.target)) {
        setVehiclePickerOpen(false);
        setVehiclePickerQuery('');
        setVehiclePickerHighlight(0);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [vehiclePickerOpen]);

  const STORAGE_KEY = 'partsLookup_productNameToArticle';

  // Pre-seeded: common part descriptions → article numbers so users don't need to know part numbers
  const PRESEED_DESCRIPTION_TO_ARTICLE = {
    'drive shaft': ['C2029'],
    'drive shafts': ['C2029'],
    'clutch release bearing': ['C2029'],
    'clutch bearing': ['C2029'],
    'release bearing': ['C2029'],
  };

  const loadProductNameMap = () => {
    const out = {};
    for (const [name, arr] of Object.entries(PRESEED_DESCRIPTION_TO_ARTICLE)) {
      out[name] = new Set(arr);
    }
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return out;
      const parsed = JSON.parse(raw);
      for (const [name, arr] of Object.entries(parsed)) {
        if (Array.isArray(arr)) out[name] = new Set([...(out[name] || []), ...arr]);
      }
    } catch {
      // keep preseed only
    }
    return out;
  };

  const saveProductNameMap = (map) => {
    try {
      const toSave = {};
      for (const [name, setVal] of Object.entries(map)) {
        if (setVal && typeof setVal.forEach === 'function') {
          const arr = [];
          setVal.forEach((an) => arr.push(an));
          toSave[name] = arr;
        }
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
    } catch (e) {
      console.warn('Could not persist parts lookup map', e);
    }
  };

  // Map product name (lowercase) → Set of article numbers. Filled from article/OEM search results.
  const productNameToArticleNumbersRef = useRef(loadProductNameMap());

  useEffect(() => {
    autoPartsService.ensureCatalog().catch(() => {});
  }, []);

  // Match VIN against local car catalog (auto-parts-db). No TecDoc fitment IDs.
  useEffect(() => {
    if (!contextVehicle) {
      setVehicleId(null);
      return;
    }
    if (contextVehicle.vehicleId != null) {
      setVehicleId(contextVehicle.vehicleId);
      return;
    }
    const vin = contextVehicle?.vin && String(contextVehicle.vin).trim();
    if (!vin || vin.length !== 17) {
      setVehicleId(null);
      return;
    }
    let cancelled = false;
    setVehicleIdLoading(true);
    autoPartsService.vinCheck(vin)
      .then((res) => {
        if (cancelled) return;
        const id = res?.vehicleId ?? res?.carId ?? res?.id ?? null;
        setVehicleId(id != null ? id : null);
      })
      .catch(() => {
        if (!cancelled) setVehicleId(null);
      })
      .finally(() => {
        if (!cancelled) setVehicleIdLoading(false);
      });
    return () => { cancelled = true; };
  }, [contextVehicle]);

  const looksLikePartNumber = (term) => {
    const t = String(term).trim().replace(/\s+/g, '');
    return t.length >= 3 && t.length <= 25 && /^[A-Za-z0-9-]+$/.test(t);
  };

  const handleAddCarByReg = async () => {
    const reg = String(adHocReg).trim().toUpperCase().replace(/\s+/g, '');
    if (!reg || reg.length < 2) {
      setAdHocError('Enter a valid registration number');
      return;
    }
    setAdHocError(null);
    setAdHocLoading(true);
    try {
      const [dvlaResult, motWebsiteResult] = await Promise.allSettled([
        dvlaLookup(reg),
        motWebsiteLookup(reg),
      ]);

      if (dvlaResult.status !== 'fulfilled') {
        throw dvlaResult.reason;
      }
      const data = dvlaResult.value;
      let make = data.make || '';
      let model = data.model || '';
      const year = data.yearOfManufacture ? String(data.yearOfManufacture) : '';

      const scraped =
        motWebsiteResult.status === 'fulfilled' ? motWebsiteResult.value : null;
      if (scraped?.make) make = scraped.make;
      if (scraped?.model) model = scraped.model;

      const displayName =
        formatVehicleDisplayName({ make, model, year }) || 'Vehicle';
      setAdHocVehicle({
        reg,
        vehicle: displayName,
        make,
        model,
        year,
        vehicleId: null,
        vin: null
      });
      setSelectedVehicleId(null);
      setResults([]);
      setSelectedPart(null);
    } catch (err) {
      setAdHocError(err.message || 'Could not look up registration. Check the number and try again.');
    } finally {
      setAdHocLoading(false);
    }
  };

  const handleLoadPartsForCar = async () => {
    const v = contextVehicle;
    if (!v || (!v.make && !v.model && !v.vehicle)) {
      setError('Select a vehicle with make/model first');
      return;
    }
    let make = v.make || '';
    let model = v.model || '';
    if ((!make || !model) && v.vehicle) {
      const parts = String(v.vehicle).replace(/\s*\([^)]*\)\s*/g, ' ').trim().split(/\s+/);
      if (!make && parts[0]) make = parts[0];
      if (!model && parts.length > 1) model = parts.slice(1).filter(p => !/^\d{4}$/.test(p)).join(' ') || parts[1];
    }
    if (!make && !model) {
      setError('Vehicle needs make and model to load parts from catalog');
      return;
    }
    setLoading(true);
    setError(null);
    setResults([]);
    setSelectedPart(null);
    try {
      const parts = await autoPartsService.getPartsForVehicleFromCatalog(
        { make, model, year: v.year },
        6,
        20
      );
      if (parts.length > 0) {
        setResults(parts);
      } else {
        setError('No parts found in catalog for this vehicle');
      }
    } catch (err) {
      setError(err.message || 'Could not load parts from catalog');
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!searchTerm.trim()) {
      setError('Please enter what you need (e.g. drive shaft, brake pad) or a part number');
      return;
    }

    setLoading(true);
    setError(null);
    setResults([]);
    setSelectedPart(null);
    setPartDetails(null);
    setCompatibleVehicles([]);

    const term = searchTerm.trim();
    const keyword = term.toLowerCase();
    const map = productNameToArticleNumbersRef.current;

    try {
      const allArticles = [];
      const seenIds = new Set();
      const addParts = (list) => {
        const arr = list?.articles && Array.isArray(list.articles) ? list.articles : Array.isArray(list) ? list : [];
        for (const part of arr) {
          const id = part.articleId ?? `${part.articleNo}-${part.supplierId}`;
          if (!seenIds.has(id)) {
            seenIds.add(id);
            allArticles.push(part);
          }
        }
      };

      // 1) If it looks like a part number, try Article Number and OEM search (users can paste a number)
      if (looksLikePartNumber(term)) {
        const partNo = term.replace(/\s+/g, '');
        try {
          const r = await autoPartsService.searchByArticleNumber(partNo);
          addParts(r);
        } catch (_) {}
        try {
          const r = await autoPartsService.searchByOEMNumber(partNo);
          addParts(r);
        } catch (_) {}
      }

      // 2) Description path: API with description text
      if (allArticles.length === 0) {
        try {
          const resp = await autoPartsService.searchByArticleNumber(term);
          const list = resp?.articles && Array.isArray(resp.articles) ? resp.articles : Array.isArray(resp) ? resp : [];
          const matchesDescription = (part) => {
            const desc = (part.articleProductName || part.productName || part.description || part.name || '').toLowerCase();
            return desc.includes(keyword) || keyword.includes(desc);
          };
          if (list.length > 0 && list.some(matchesDescription)) {
            addParts({ articles: list });
          }
        } catch (_) {}
      }

      // 3) Description path: preseed + cache (description → article numbers)
      if (allArticles.length === 0) {
        const articleNumbersToFetch = new Set();
        for (const [productName, articleNumbers] of Object.entries(map)) {
          if (productName.includes(keyword) && articleNumbers && articleNumbers.size) {
            articleNumbers.forEach((an) => articleNumbersToFetch.add(an));
          }
        }
        if (articleNumbersToFetch.size > 0) {
          const promises = Array.from(articleNumbersToFetch).map((articleNo) =>
            autoPartsService.searchByArticleNumber(articleNo)
          );
          const responses = await Promise.all(promises);
          for (const r of responses) addParts(r);
        }
      }

      // 4) listProductNames: catalog may have description + article number
      if (allArticles.length === 0) {
        try {
          const raw = await autoPartsService.listProductNames();
          const productNames = Array.isArray(raw)
            ? raw
            : (raw && Array.isArray(raw.products) ? raw.products : raw?.data || []);
          const withArticleNo = [];
          for (const item of productNames) {
            const name = typeof item === 'string' ? item : (item?.name || item?.productName || item?.description || item?.articleProductName || item?.productGroupName || '');
            if (!String(name).toLowerCase().includes(keyword)) continue;
            const articleNo = item?.articleNo ?? item?.articleNumber ?? item?.mfrNumber;
            if (articleNo) withArticleNo.push(articleNo);
          }
          for (const articleNo of [...new Set(withArticleNo)].slice(0, 10)) {
            const r = await autoPartsService.searchByArticleNumber(articleNo);
            addParts(r);
          }
        } catch (_) {}
      }

      let resultsArray = allArticles;

      // Build description → article number map from any results (so future searches work)
      if (resultsArray.length > 0) {
        const map = productNameToArticleNumbersRef.current;
        for (const part of resultsArray) {
          const name = part.articleProductName || part.productName || part.description || part.name;
          const articleNo = part.articleNo || part.articleNumber || part.mfrNumber;
          if (name && articleNo) {
            const key = String(name).toLowerCase();
            if (!map[key]) map[key] = new Set();
            map[key].add(articleNo);
          }
        }
        saveProductNameMap(map);
      }

      // car-api has no per-part vehicle fitment — show all keyword matches
      
      if (resultsArray.length > 0) {
        if (resultsArray[0] && typeof resultsArray[0] === 'object') {
          console.log('First result fields:', Object.keys(resultsArray[0]));
        }
        setResults(resultsArray);
        setError(null);
      } else {
        setResults([]);
        setError(
          'No parts found. Try another description (e.g. brake, filter, absorber) or a part slug.'
        );
      }
    } catch (err) {
      console.error('Parts search error:', err);
      setError(`Search failed: ${err.message || 'Please try again'}`);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePartClick = async (part) => {
    setSelectedPart(part);
    setCompatibleVehicles([]);
    
    // If part has an ID, fetch media/images
    if (part.articleId || part.id) {
      setLoading(true);
      try {
        const media = await autoPartsService.getArticleMedia(part.articleId || part.id);
        setPartDetails({ ...part, media });
      } catch (err) {
        console.error('Error fetching part details:', err);
        // Just use the part data without additional details
        setPartDetails(part);
      } finally {
        setLoading(false);
      }
    } else {
      setPartDetails(part);
    }
  };

  const handleSearchByNumber = async (number, type) => {
    const trimmed = (number || '').trim();
    if (!trimmed) return;
    setSearchTerm(trimmed);
    setError(null);
    setResults([]);
    setSelectedPart(null);
    setPartDetails(null);
    setCompatibleVehicles([]);
    setLoading(true);
    try {
      let data;
      if (type === 'oem') {
        data = await autoPartsService.searchByOEMNumber(trimmed);
      } else {
        data = await autoPartsService.searchByArticleNumber(trimmed);
      }
      let resultsArray = [];
      if (data && data.articles && Array.isArray(data.articles)) {
        resultsArray = data.articles;
      } else if (data && Array.isArray(data)) {
        resultsArray = data;
      } else if (data && !Array.isArray(data) && typeof data === 'object') {
        resultsArray = [data];
      }
      if (resultsArray.length > 0) {
        setResults(resultsArray);
        setError(null);
      } else {
        setResults([]);
        setError('No parts found for that number.');
      }
    } catch (err) {
      console.error('Parts search error:', err);
      setError(`Search failed: ${err.message || 'Please try again'}`);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleShowCompatibility = async (part) => {
    setShowingCompatibility(true);
    try {
      const articleNo = part.articleNumber || part.articleNo;
      if (articleNo) {
        const vehicles = await autoPartsService.getCompatibleCarsByArticleNumber(articleNo);
        setCompatibleVehicles(vehicles || []);
      }
    } catch (err) {
      console.error('Error fetching compatible vehicles:', err);
      setCompatibleVehicles([]);
      setError('Could not load compatible vehicles');
    } finally {
      setShowingCompatibility(false);
    }
  };

  const handleAddPart = () => {
    if (selectedPart && onPartSelected) {
      // Use articleProductName first (API field); fallbacks for other response shapes
      const description = selectedPart.articleProductName || selectedPart.description || selectedPart.name || selectedPart.productName || 'Unknown Part';
      const articleNumber = selectedPart.articleNo || selectedPart.articleNumber || 'N/A';
      const supplier = selectedPart.supplierName || selectedPart.supplier || 'Unknown';

      const partData = {
        articleNumber,
        description,
        articleProductName: selectedPart.articleProductName || null,
        supplier,
        supplierId: selectedPart.supplierId || null,
        oemNumber: selectedPart.oemNumber || null,
        quantity: 1,
        addedDate: new Date().toISOString(),
        details: partDetails || null,
        articleId: selectedPart.articleId || null,
        s3image: selectedPart.s3image || null
      };
      
      onPartSelected(partData);
      
      // Reset form
      setSearchTerm('');
      setResults([]);
      setSelectedPart(null);
      setPartDetails(null);
    }
  };

  return (
    <div
      className={embedded ? 'parts-lookup-embedded' : 'modal-backdrop'}
      style={embedded ? { width: '100%' } : { display: 'flex' }}
      onClick={embedded ? undefined : onClose}
    >
      <div
        className={embedded ? undefined : 'modal'}
        style={embedded ? undefined : { maxWidth: '900px', maxHeight: '90vh' }}
        onClick={embedded ? undefined : (e) => e.stopPropagation()}
      >
        {!embedded && (
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label="Close parts lookup"
          >
            ×
          </button>
        )}

        {!embedded && <h3 style={{ marginBottom: '16px' }}>Parts Lookup</h3>}

        {/* Vehicle context — always visible so user knows which car parts are for */}
        <div style={{
          background: contextVehicle ? 'var(--light-bg)' : 'rgba(239, 68, 68, 0.08)',
          border: contextVehicle ? 'none' : '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '12px',
          padding: '12px 16px',
          marginBottom: '16px',
          fontSize: '14px',
          color: 'var(--text-muted)'
        }}>
          {isStandalone && vehicles.length > 0 && (
            <div style={{ marginBottom: '12px' }} ref={vehiclePickerRef}>
              <label htmlFor="parts-vehicle-select" style={{ display: 'block', fontWeight: '600', color: 'var(--text-dark)', marginBottom: '6px' }}>
                Search parts for:
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  id="parts-vehicle-select"
                  ref={vehicleInputRef}
                  type="text"
                  role="combobox"
                  aria-expanded={vehiclePickerOpen}
                  aria-controls="parts-vehicle-listbox"
                  aria-autocomplete="list"
                  autoComplete="off"
                  value={vehiclePickerOpen ? vehiclePickerQuery : vehiclePickerDisplay}
                  placeholder={vehiclePickerOpen ? 'Type reg, make or model…' : '— No vehicle selected (all parts) —'}
                  onFocus={() => {
                    setVehiclePickerOpen(true);
                    setVehiclePickerQuery('');
                    setVehiclePickerHighlight(0);
                  }}
                  onClick={() => {
                    setVehiclePickerOpen(true);
                    setVehiclePickerQuery('');
                    setVehiclePickerHighlight(0);
                  }}
                  onChange={(e) => {
                    setVehiclePickerOpen(true);
                    setVehiclePickerQuery(e.target.value);
                    setVehiclePickerHighlight(0);
                  }}
                  onKeyDown={(e) => {
                    if (!vehiclePickerOpen && (e.key === 'ArrowDown' || e.key === 'Enter')) {
                      setVehiclePickerOpen(true);
                      setVehiclePickerQuery('');
                      setVehiclePickerHighlight(0);
                      return;
                    }
                    if (!vehiclePickerOpen) return;
                    const optionCount = filteredPickerVehicles.length + 1; // + clear option
                    if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      setVehiclePickerHighlight((h) => (h + 1) % optionCount);
                    } else if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      setVehiclePickerHighlight((h) => (h - 1 + optionCount) % optionCount);
                    } else if (e.key === 'Enter') {
                      e.preventDefault();
                      if (vehiclePickerHighlight === 0) {
                        clearVehicleSelection();
                        setVehiclePickerOpen(false);
                        setVehiclePickerQuery('');
                      } else {
                        const v = filteredPickerVehicles[vehiclePickerHighlight - 1];
                        if (v) selectGarageVehicle(v.id);
                      }
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      setVehiclePickerOpen(false);
                      setVehiclePickerQuery('');
                      setVehiclePickerHighlight(0);
                      vehicleInputRef.current?.blur();
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 36px 10px 12px',
                    borderRadius: '8px',
                    border: '1px solid var(--border)',
                    fontSize: '14px',
                    background: 'var(--white-bg)',
                    boxSizing: 'border-box'
                  }}
                />
                <span
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none',
                    color: 'var(--text-muted)',
                    fontSize: '12px'
                  }}
                >
                  {vehiclePickerOpen ? '▲' : '▼'}
                </span>
                {vehiclePickerOpen && (
                  <ul
                    id="parts-vehicle-listbox"
                    role="listbox"
                    style={{
                      position: 'absolute',
                      zIndex: 30,
                      left: 0,
                      right: 0,
                      top: 'calc(100% + 4px)',
                      margin: 0,
                      padding: '6px 0',
                      listStyle: 'none',
                      maxHeight: 'min(280px, 45vh)',
                      overflowY: 'auto',
                      background: 'var(--white-bg)',
                      border: '1px solid var(--border)',
                      borderRadius: '8px',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
                    }}
                  >
                    <li role="option" aria-selected={!selectedVehicleId && !adHocVehicle}>
                      <button
                        type="button"
                        onMouseEnter={() => setVehiclePickerHighlight(0)}
                        onClick={() => {
                          clearVehicleSelection();
                          setVehiclePickerOpen(false);
                          setVehiclePickerQuery('');
                        }}
                        style={{
                          display: 'block',
                          width: '100%',
                          textAlign: 'left',
                          padding: '10px 12px',
                          border: 'none',
                          background: vehiclePickerHighlight === 0 ? 'var(--light-bg)' : 'transparent',
                          color: 'var(--text-muted)',
                          fontSize: '14px',
                          cursor: 'pointer',
                          minHeight: '44px'
                        }}
                      >
                        — No vehicle selected (all parts) —
                      </button>
                    </li>
                    {adHocVehicle && !vehiclePickerQuery.trim() && (
                      <li role="option" aria-selected>
                        <button
                          type="button"
                          onClick={() => {
                            setVehiclePickerOpen(false);
                            setVehiclePickerQuery('');
                          }}
                          style={{
                            display: 'block',
                            width: '100%',
                            textAlign: 'left',
                            padding: '10px 12px',
                            border: 'none',
                            background: 'var(--light-bg)',
                            color: 'var(--text-dark)',
                            fontSize: '14px',
                            cursor: 'pointer',
                            minHeight: '44px'
                          }}
                        >
                          {adHocVehicle.reg} — {adHocVehicle.vehicle}
                        </button>
                      </li>
                    )}
                    {filteredPickerVehicles.length === 0 ? (
                      <li style={{ padding: '12px', color: 'var(--text-muted)', fontSize: '13px' }}>
                        No vehicles match &quot;{vehiclePickerQuery}&quot;
                      </li>
                    ) : (
                      filteredPickerVehicles.map((v, idx) => {
                        const active = vehiclePickerHighlight === idx + 1;
                        const selected = selectedVehicleId === v.id && !adHocVehicle;
                        return (
                          <li key={v.id} role="option" aria-selected={selected}>
                            <button
                              type="button"
                              onMouseEnter={() => setVehiclePickerHighlight(idx + 1)}
                              onClick={() => selectGarageVehicle(v.id)}
                              style={{
                                display: 'block',
                                width: '100%',
                                textAlign: 'left',
                                padding: '10px 12px',
                                border: 'none',
                                background: active || selected ? 'var(--light-bg)' : 'transparent',
                                color: 'var(--text-dark)',
                                fontSize: '14px',
                                cursor: 'pointer',
                                minHeight: '44px'
                              }}
                            >
                              {formatVehicleOption(v)}
                            </button>
                          </li>
                        );
                      })
                    )}
                  </ul>
                )}
              </div>
            </div>
          )}
          {isStandalone && (
            <div style={{ marginBottom: adHocVehicle ? '12px' : 0 }}>
              {adHocVehicle ? (
                <button
                  type="button"
                  onClick={() => {
                    setAdHocVehicle(null);
                    setAdHocReg('');
                    setAdHocError(null);
                    setResults([]);
                    setSelectedPart(null);
                  }}
                  style={{
                    fontSize: '13px',
                    color: 'var(--primary)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    textDecoration: 'underline'
                  }}
                >
                  Clear and choose another
                </button>
              ) : (
                <>
                  <label htmlFor="parts-adhoc-reg" style={{ display: 'block', fontWeight: '600', color: 'var(--text-dark)', marginBottom: '6px' }}>
                    Or add a car by registration (not in garage):
                  </label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <input
                      id="parts-adhoc-reg"
                      type="text"
                      value={adHocReg}
                      onChange={(e) => {
                        setAdHocReg(e.target.value.toUpperCase());
                        setAdHocError(null);
                      }}
                      placeholder="e.g. AB12 CDE"
                      style={{
                        flex: 1,
                        minWidth: '160px',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        border: '1px solid var(--border)',
                        fontSize: '14px'
                      }}
                    />
                    <button
                      type="button"
                      className="primary-btn"
                      onClick={handleAddCarByReg}
                      disabled={adHocLoading || !adHocReg.trim()}
                      style={{ minWidth: '120px' }}
                    >
                      {adHocLoading ? 'Looking up…' : 'Use this car'}
                    </button>
                  </div>
                  {adHocError && (
                    <div style={{ fontSize: '12px', color: '#DC2626', marginTop: '6px' }}>{adHocError}</div>
                  )}
                </>
              )}
            </div>
          )}
          {contextVehicle ? (
            <>
              <div style={{ marginBottom: vehicleIdLoading || vehicleId != null || !contextVehicle?.vin ? '6px' : 0 }}>
                <strong style={{ color: 'var(--text-dark)' }}>{contextVehicle.reg}</strong>
                {' — '}
                {contextVehicle.vehicle || 'Unknown Vehicle'}
                {contextVehicle.make && (
                  <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>
                    {' '}({[contextVehicle.make, contextVehicle.model, contextVehicle.year].filter(Boolean).join(' ')})
                  </span>
                )}
              </div>
              {vehicleIdLoading && (
                <div style={{ fontSize: '12px', color: 'var(--primary)' }}>Checking vehicle…</div>
              )}
              {!vehicleIdLoading && vehicleId != null && (
                <div style={{ fontSize: '12px', color: '#059669', fontWeight: '600' }}>
                  Matched in car catalog — use “Load parts for this car” or search by part name
                </div>
              )}
              {!vehicleIdLoading && vehicleId == null && contextVehicle?.vin && (
                <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                  VIN not matched in car catalog — search still works for all parts
                </div>
              )}
              {!vehicleIdLoading && adHocVehicle && (
                <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                  Vehicle from DVLA — catalog validates make/model, then shows local parts list
                </div>
              )}
            </>
          ) : (
            <div style={{ fontSize: '14px', color: 'var(--text-dark)' }}>
              {isStandalone && vehicles.length > 0 ? (
                <>Select a vehicle above to search against that car’s make/model.</>
              ) : isStandalone ? (
                <>Open a vehicle first, or use Parts Lookup from the vehicle detail to search for that car.</>
              ) : null}
            </div>
          )}
        </div>

        {/* Load parts for this car from catalog (needs make/model or vehicle name to parse) */}
        {contextVehicle && (contextVehicle.make || contextVehicle.model || contextVehicle.vehicle) && (
          <div style={{ marginBottom: '16px' }}>
            <button
              type="button"
              className="primary-btn"
              onClick={handleLoadPartsForCar}
              disabled={loading}
              style={{
                padding: '10px 20px',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              {loading ? 'Loading…' : 'Load parts for this car from catalog'}
            </button>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '6px' }}>
              Checks make/model in the local car catalog (auto-parts-db), then lists catalog parts.
            </div>
          </div>
        )}

        {/* Single search: description or part number — no need to know article numbers */}
        <form onSubmit={handleSearch} style={{ marginBottom: '24px' }}>
          <div className="form-row">
            <label htmlFor="partsSearch">What do you need?</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                id="partsSearch"
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="e.g. drive shaft, brake pad, oil filter, or part number"
                style={{ flex: 1 }}
                disabled={loading}
              />
              <button
                type="submit"
                className="primary-btn"
                disabled={loading || !searchTerm.trim()}
                style={{ minWidth: '100px' }}
              >
                {loading ? 'Searching...' : 'Search'}
              </button>
            </div>
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
            Search by part name (e.g. drive shaft) or paste a part number if you have one.
          </div>
        </form>

        {/* Error Message */}
        {error && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '16px',
            color: '#DC2626',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        {/* Results */}
        {results.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <h4 style={{ marginBottom: '12px', fontSize: '15px', fontWeight: '600' }}>
              Search Results ({filteredResults.length}
              {resultsCategoryFilter !== 'All' ? ` in ${resultsCategoryFilter}` : ''}
              {filteredResults.length !== results.length ? ` of ${results.length}` : ''})
              {contextVehicle && (
                <span style={{ fontWeight: 500, color: 'var(--text-muted)', fontSize: '13px' }}>
                  {' '}for {contextVehicle.reg} — {contextVehicle.vehicle || 'Unknown'}
                </span>
              )}
            </h4>

            {/* Category chips */}
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '6px',
                marginBottom: '10px',
              }}
            >
              {Object.entries(categoryCounts)
                .sort(([a], [b]) => {
                  if (a === 'All') return -1;
                  if (b === 'All') return 1;
                  return a.localeCompare(b);
                })
                .map(([cat, count]) => {
                  const active = resultsCategoryFilter === cat;
                  return (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => setResultsCategoryFilter(cat)}
                      style={{
                        minHeight: '36px',
                        padding: '6px 12px',
                        borderRadius: '999px',
                        border: active ? '1px solid var(--primary)' : '1px solid var(--border)',
                        background: active ? 'rgba(79, 141, 243, 0.12)' : 'var(--white-bg)',
                        color: active ? 'var(--primary)' : 'var(--text-dark)',
                        fontSize: '12px',
                        fontWeight: active ? 700 : 500,
                        cursor: 'pointer',
                      }}
                    >
                      {cat} ({count})
                    </button>
                  );
                })}
            </div>

            <div style={{
              maxHeight: '420px',
              overflowY: 'auto',
              border: '1px solid var(--border)',
              borderRadius: '8px'
            }}>
              {groupedResults.length === 0 ? (
                <div style={{ padding: '16px', color: 'var(--text-muted)', fontSize: '13px' }}>
                  No parts in this category.
                </div>
              ) : (
                groupedResults.map(({ category, parts }) => (
                  <div key={category}>
                    <div
                      style={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                        padding: '8px 16px',
                        background: 'var(--light-bg)',
                        borderBottom: '1px solid var(--border)',
                        fontSize: '12px',
                        fontWeight: 700,
                        color: 'var(--text-muted)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                      }}
                    >
                      {category} ({parts.length})
                    </div>
                    {parts.map((part, index) => (
                      <div
                        key={part.slug || part.articleId || `${category}-${index}`}
                        onClick={() => handlePartClick(part)}
                        style={{
                          padding: '12px 16px',
                          borderBottom: '1px solid var(--border)',
                          cursor: 'pointer',
                          background: selectedPart === part || selectedPart?.slug === part.slug
                            ? 'rgba(79, 141, 243, 0.05)'
                            : 'transparent',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          if (selectedPart !== part && selectedPart?.slug !== part.slug) {
                            e.currentTarget.style.background = 'var(--light-bg)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (selectedPart !== part && selectedPart?.slug !== part.slug) {
                            e.currentTarget.style.background = 'transparent';
                          }
                        }}
                      >
                        <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {part.s3image && (
                            <img
                              src={part.s3image}
                              alt={part.articleProductName || 'Part'}
                              style={{ width: '32px', height: '32px', objectFit: 'contain', borderRadius: '4px' }}
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          )}
                          <span>
                            {part.articleProductName || part.description || part.name || part.productName || part.genericArticleDescription || 'Unknown Part'}
                          </span>
                        </div>
                        <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                          {(part.articleNo || part.articleNumber || part.mfrNumber) && (
                            <span>Article: {part.articleNo || part.articleNumber || part.mfrNumber}</span>
                          )}
                          <span style={{ marginLeft: part.articleNo || part.articleNumber ? '12px' : 0 }}>
                            {part.category || getPartCategory(part)}
                          </span>
                        </div>
                        <PartSupplierLinks part={part} vehicle={contextVehicle} compact />
                      </div>
                    ))}
                  </div>
                ))
              )}
            </div>
            {contextVehicle?.reg && (
              <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--text-muted)' }}>
                Supplier links open GSF / Euro Car Parts with reg{' '}
                <strong>{String(contextVehicle.reg).toUpperCase().replace(/[^A-Z0-9]/g, '')}</strong>{' '}
                selected — browse their categories there for the part you need.
              </div>
            )}
          </div>
        )}

        {/* Selected Part Details */}
        {selectedPart && (
          <div style={{
            background: 'var(--light-bg)',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '16px',
            border: '2px solid var(--primary)'
          }}>
            <h4 style={{ marginBottom: '12px', fontSize: '15px', fontWeight: '600', color: 'var(--primary)' }}>
              Selected Part
            </h4>
            
            {selectedPart.s3image && (
              <div style={{ marginBottom: '12px', textAlign: 'center' }}>
                <img 
                  src={selectedPart.s3image} 
                  alt={selectedPart.articleProductName || 'Part'} 
                  style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'contain', borderRadius: '8px' }}
                  onError={(e) => e.target.style.display = 'none'}
                />
              </div>
            )}

            <div style={{ fontSize: '14px', marginBottom: '8px' }}>
              <strong>Description:</strong> {selectedPart.articleProductName || selectedPart.description || selectedPart.name || selectedPart.productName || 'N/A'}
            </div>
            
            {(selectedPart.articleNo || selectedPart.articleNumber || selectedPart.mfrNumber) && (
              <div style={{ fontSize: '14px', marginBottom: '8px' }}>
                <strong>Article Number:</strong> {selectedPart.articleNo || selectedPart.articleNumber || selectedPart.mfrNumber}
              </div>
            )}
            
            {(selectedPart.supplierName || selectedPart.supplier || selectedPart.brandName || selectedPart.mfrName) && (
              <div style={{ fontSize: '14px', marginBottom: '8px' }}>
                <strong>Supplier:</strong> {selectedPart.supplierName || selectedPart.supplier || selectedPart.brandName || selectedPart.mfrName}
              </div>
            )}

            {selectedPart.articleId && (
              <div style={{ fontSize: '14px', marginBottom: '8px' }}>
                <strong>Article ID:</strong> {selectedPart.articleId}
              </div>
            )}
            
            {selectedPart.oemNumber && (
              <div style={{ fontSize: '14px', marginBottom: '8px' }}>
                <strong>OEM Number:</strong> {selectedPart.oemNumber}
              </div>
            )}

            <div style={{ fontSize: '14px', marginBottom: '8px' }}>
              <strong>Category:</strong> {selectedPart.category || getPartCategory(selectedPart)}
            </div>

            <div
              style={{
                marginTop: '12px',
                padding: '12px',
                background: 'var(--white-bg)',
                borderRadius: '8px',
                border: '1px solid var(--border)',
              }}
            >
              <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '6px', color: 'var(--text-dark)' }}>
                Check suppliers by registration
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '8px' }}>
                {contextVehicle?.reg
                  ? `Opens GSF / Euro Car Parts hubs with reg ${String(contextVehicle.reg).toUpperCase().replace(/[^A-Z0-9]/g, '')} selected — then pick the category for this part.`
                  : 'Select a garage vehicle (or add a reg) first. Both sites look up parts by registration, not part name.'}
              </div>
              <PartSupplierLinks part={selectedPart} vehicle={contextVehicle} />
            </div>

            {selectedPart.keywordMatch && !(selectedPart.articleNo || selectedPart.articleNumber) && (
              <div style={{
                marginTop: '12px',
                padding: '12px',
                background: 'rgba(79, 141, 243, 0.08)',
                borderRadius: '8px',
                border: '1px solid rgba(79, 141, 243, 0.3)',
                fontSize: '13px',
                color: 'var(--text-muted)'
              }}>
                <div style={{ marginBottom: '12px' }}>
                  This is a description match. Enter an Article number or OEM number below to find and add the specific part.
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <label style={{ minWidth: '100px', fontWeight: '600', color: 'var(--text-dark)' }}>Article number:</label>
                    <input
                      type="text"
                      value={inlineArticleNo}
                      onChange={(e) => setInlineArticleNo(e.target.value)}
                      placeholder="e.g. C2029"
                      style={{
                        flex: 1,
                        minWidth: '120px',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        border: '1px solid var(--border)',
                        fontSize: '13px'
                      }}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearchByNumber(inlineArticleNo, 'article')}
                    />
                    <button
                      type="button"
                      onClick={() => handleSearchByNumber(inlineArticleNo, 'article')}
                      disabled={loading || !inlineArticleNo.trim()}
                      style={{
                        padding: '8px 14px',
                        background: 'var(--primary, #4f8df3)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: loading || !inlineArticleNo.trim() ? 'not-allowed' : 'pointer',
                        opacity: loading || !inlineArticleNo.trim() ? 0.6 : 1
                      }}
                    >
                      {loading ? 'Searching...' : 'Find part'}
                    </button>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <label style={{ minWidth: '100px', fontWeight: '600', color: 'var(--text-dark)' }}>OEM number:</label>
                    <input
                      type="text"
                      value={inlineOemNo}
                      onChange={(e) => setInlineOemNo(e.target.value)}
                      placeholder="e.g. 8F0513035N"
                      style={{
                        flex: 1,
                        minWidth: '120px',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        border: '1px solid var(--border)',
                        fontSize: '13px'
                      }}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearchByNumber(inlineOemNo, 'oem')}
                    />
                    <button
                      type="button"
                      onClick={() => handleSearchByNumber(inlineOemNo, 'oem')}
                      disabled={loading || !inlineOemNo.trim()}
                      style={{
                        padding: '8px 14px',
                        background: 'var(--primary, #4f8df3)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: loading || !inlineOemNo.trim() ? 'not-allowed' : 'pointer',
                        opacity: loading || !inlineOemNo.trim() ? 0.6 : 1
                      }}
                    >
                      {loading ? 'Searching...' : 'Find part'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {partDetails && (
              <div style={{ 
                marginTop: '12px', 
                paddingTop: '12px', 
                borderTop: '1px solid var(--border)',
                fontSize: '13px',
                color: 'var(--text-muted)'
              }}>
                <div><strong>Additional Details Available</strong></div>
              </div>
            )}

            {/* Show Compatible Vehicles Button */}
            {(selectedPart.articleNumber || selectedPart.articleNo) && (
              <button
                type="button"
                onClick={() => handleShowCompatibility(selectedPart)}
                disabled={showingCompatibility}
                style={{
                  marginTop: '12px',
                  padding: '8px 16px',
                  background: 'var(--white-bg)',
                  border: '1px solid var(--primary)',
                  color: 'var(--primary)',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: showingCompatibility ? 'not-allowed' : 'pointer',
                  opacity: showingCompatibility ? 0.6 : 1,
                  width: '100%'
                }}
              >
                {showingCompatibility ? 'Loading...' : 'Show Compatible Vehicles'}
              </button>
            )}
          </div>
        )}

        {/* Compatible Vehicles */}
        {compatibleVehicles.length > 0 && (
          <div style={{
            background: 'rgba(86, 197, 150, 0.08)',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '16px',
            border: '1px solid rgba(86, 197, 150, 0.3)'
          }}>
            <h4 style={{ marginBottom: '12px', fontSize: '15px', fontWeight: '600', color: '#279268' }}>
              Compatible Vehicles ({compatibleVehicles.length})
            </h4>
            <div style={{
              maxHeight: '200px',
              overflowY: 'auto',
              fontSize: '13px'
            }}>
              {compatibleVehicles.slice(0, 10).map((veh, index) => (
                <div
                  key={index}
                  style={{
                    padding: '8px 12px',
                    background: 'var(--white-bg)',
                    borderRadius: '6px',
                    marginBottom: '6px',
                    display: 'flex',
                    justifyContent: 'space-between'
                  }}
                >
                  <span style={{ fontWeight: '500' }}>
                    {veh.manufacturer || veh.make || 'Unknown'} {veh.model || ''}
                  </span>
                  <span style={{ color: 'var(--text-muted)' }}>
                    {veh.year || veh.yearFrom ? `(${veh.year || veh.yearFrom})` : ''}
                  </span>
                </div>
              ))}
              {compatibleVehicles.length > 10 && (
                <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '12px', marginTop: '8px' }}>
                  + {compatibleVehicles.length - 10} more vehicles
                </div>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="form-actions">
          {!embedded && (
            <button
              type="button"
              className="secondary-btn"
              onClick={onClose}
            >
              Close
            </button>
          )}

          {selectedPart && (selectedPart.articleNo || selectedPart.articleNumber) && onPartSelected && (
            <button
              type="button"
              className="primary-btn"
              onClick={handleAddPart}
            >
              Add Part to Vehicle
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PartsLookup;
